import { Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest
} from '@angular/common/http';
import { defer, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { AuthTokenService } from './auth-token.service';
import { LoginService } from '../Login/Login.service';
import { coordinateRefresh } from './auth-refresh-coordinator';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private refreshInFlight$: Observable<string> | null = null;

  constructor(
    private tokens: AuthTokenService,
    private router: Router,
    private injector: Injector
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApiRequest = request.url.indexOf(environment.BaseUrl) === 0;
    const isLogin = /\/Daro\/Login\/?$/i.test(request.url);
    const isRefresh = /\/Daro\/Login\/Refresh\/?$/i.test(request.url);
    const isLogout = /\/Daro\/Login\/Exit\/?$/i.test(request.url);
    const skipBearer = isLogin || isRefresh;
    const skipAutoRefresh = isLogin || isRefresh || isLogout;
    const tokenStrippedRequest = isApiRequest && !skipBearer
      ? this.stripLegacyTokenQuery(request)
      : request;
    const hasAuthorizationHeader = tokenStrippedRequest.headers.has('Authorization');
    const accessToken = isApiRequest && !skipBearer && !hasAuthorizationHeader
      ? this.tokens.getAccessToken()
      : null;
    const outgoing = accessToken
      ? tokenStrippedRequest.clone({ setHeaders: { Authorization: 'Bearer ' + accessToken } })
      : tokenStrippedRequest;

    return next.handle(outgoing).pipe(
      catchError((error: HttpErrorResponse) => {
        if (isApiRequest && !skipAutoRefresh && !hasAuthorizationHeader && error.status === 401) {
          return this.refreshAccessToken(accessToken).pipe(
            catchError(refreshError => this.failAuthentication(refreshError, accessToken)),
            switchMap(newAccessToken => {
              const retry = tokenStrippedRequest.clone({
                setHeaders: { Authorization: 'Bearer ' + newAccessToken }
              });
              return next.handle(retry).pipe(
                // A successfully refreshed session is not invalidated by an
                // individual endpoint denying the retry. Never refresh twice.
                catchError(retryError => throwError(retryError))
              );
            })
          );
        }

        // Login/Refresh/Exit and explicitly supplied credentials own their errors.
        // 403 and 404 intentionally leave authentication storage untouched.
        return throwError(error);
      })
    );
  }

  private refreshAccessToken(failedAccessToken: string | null): Observable<string> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = coordinateRefresh(this.tokens, failedAccessToken, () => defer(() => this.injector.get(LoginService).Refresh()).pipe(
        map(response => {
          const accessToken = response && response.accessToken;
          if (!accessToken || !accessToken.trim()) {
            throw new Error('Refresh response did not include an access token.');
          }

          if (this.tokens.getAccessToken() !== failedAccessToken) {
            throw new Error('Authentication changed during refresh.');
          }
          this.tokens.storeAccessToken(accessToken);
          return accessToken;
        }))).pipe(
        finalize(() => { this.refreshInFlight$ = null; }),
        shareReplay(1)
      );
    }

    return this.refreshInFlight$;
  }

  private failAuthentication(error: any, failedAccessToken: string | null): Observable<string> {
    const current = this.tokens.getAccessToken();
    if (current && current !== failedAccessToken) {
      return of(current);
    }
    const code = error && error.error && error.error.code;
    // Only a conclusive refresh 401 invalidates credentials. Transient errors
    // and a rejected competing rotation leave recovery possible.
    if (error instanceof HttpErrorResponse && error.status === 401 &&
        code !== 'RefreshVersionMismatch' && code !== 'RefreshRotationConflict') {
      this.tokens.clearAuthentication();
      this.router.navigate(['/Login']);
    }
    return throwError(error);
  }

  private stripLegacyTokenQuery(request: HttpRequest<any>): HttpRequest<any> {
    try {
      const url = new URL(request.urlWithParams);
      const legacyTokenKeys: string[] = [];
      url.searchParams.forEach((_, key) => {
        if (key.toLowerCase() === 'token') {
          legacyTokenKeys.push(key);
        }
      });

      if (legacyTokenKeys.length === 0) {
        return request;
      }

      legacyTokenKeys.forEach(key => url.searchParams.delete(key));
      return request.clone({
        url: url.toString(),
        params: new HttpParams()
      });
    } catch {
      return request;
    }
  }
}
